<template>
  <div :class="mainContainerClass">
    <div v-if="!customRateAndTermCheckbox && !loading">
      <div class="row">
        <div class="col-md-12">
          <div class="form-group">
            <select
              v-model="rateTermSelected"
              name="iry"
              class="form-control input-md"
              required
              @change="setRateAndTerm" >
              <option v-if="showDefaultValue" value="" disabled selected hidden>Select rate and term</option>
              <option v-for="(item, index) in ratesAndTerms" :value="item" :key="index">{{item.label}}</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <div v-if="customRateAndTermCheckbox && !loading">
      <div class="row">
        <div class="col-md-12">
          <div class="row">
            <div class="col-md-5 padding-right-0">
              <div class="input-group">
                <input type="text"
                  name="editedRate"
                  id="customRate"
                  v-money="money"
                  v-model="editedRate"
                  placeholder=""
                  :class="{ 'form-control text-right': true, 'form-control-error': !isAValidRate() }"
                  v-validate="{required: true}"
                  aria-describedby="basic-addon2" />
                <span class="input-group-addon" id="basic-addon2">%</span>
              </div>
            </div>

            <div class="col-md-5 padding-right-0">
              <div class="form-group margin-0">
                <input type="text"
                  name="editedTerm"
                  id="custom-term-input"
                  v-money="money"
                  v-model="editedTerm" placeholder=""
                  :class="{ 'form-control margin-0 text-right': true, 'form-control-error': !isAValidTerm() }"
                  v-validate="{required: true}" />
              </div>
            </div>

            <div class="col-md-2 padding-right-0">
              <div class="form-group margin-0">
                <span>Years</span>
              </div>
            </div>
          </div>
        </div>

        <div class="col-md-12">
          <div class="row">
            <div class="col-md-5 padding-right-0">
              <span v-if="!isAValidRate()" class="error-feedback">
                {{ this.rateErrorMessage }}
              </span>
            </div>

            <div class="col-md-5 padding-right-0 term-error-message">
              <span v-if="!isAValidTerm()" class="error-feedback">
                {{ this.termErrorMessage }}
              </span>
            </div>
          </div>
        </div>

      </div>
    </div>

    <div v-if="!loading">
      <div class="row">
        <div class="col-md-12">
          <div class="checkbox c-checkbox check-margin">
            <label>
              <input type="checkbox"
                v-model="customRateAndTermCheckbox" />
              <span class="fa fa-check"></span>
              Add a custom rate and term
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import {
    mapState,
    mapActions,
  } from 'vuex';

  import {
    VMoney,
  } from 'v-money';

  import { Events } from '@/data/static/events';

  import {
    formatBaseAmount,
    formatRate,
    selectRateAndTermToDisplay,
  } from '@/util';

  export default {
    name: 'customRateTerm',
    async beforeMount() {
      this.loading = true;
      if (!this.isCurrentRateAndTerm) {
        const rateAndTermGroups = this.investmentsRestrictions.rateAndTermGroups;

        let rateAndTermGroup =
        rateAndTermGroups[this.manageRateAndTermGroupsId] ||
        await this.getInvestmentOptions(this.manageRateAndTermGroupsId);

        if (rateAndTermGroup.overrideManageRateAndTermGroupsId) {
          rateAndTermGroup =
          rateAndTermGroups[rateAndTermGroup.overrideManageRateAndTermGroupsId] ||
          await this.getInvestmentOptions(
            rateAndTermGroup.overrideManageRateAndTermGroupsId,
          );
        }

        this.ratesAndTerms = rateAndTermGroup.investmentOptions;
      } else {
        this.ratesAndTerms = this.investmentsRestrictions.ratesAndTerms;
      }

      if (this.showDefaultValue) {
        this.rateTermSelected = this.ratesAndTerms[0];
        this.setRateAndTerm();
        this.loading = false;
        return;
      }

      const { selected: displayRateAndTerm, isCustom: isCustomRateAndTerm } =
        selectRateAndTermToDisplay(this.nt, this.ratesAndTerms);

      if (!isCustomRateAndTerm) {
        this.rateTermSelected = displayRateAndTerm;
        this.setRateAndTerm();
      } else {
        this.editedRate = formatBaseAmount(displayRateAndTerm.rate);
        this.editedTerm = formatBaseAmount(displayRateAndTerm.term / 12);
        this.customRateAndTermCheckbox = true;
      }
      this.isAValidRate();
      this.isAValidTerm();
      this.loading = false;
    },
    data() {
      return {
        customRateAndTermCheckbox: false,
        editedRate: '0.00',
        editedTerm: '1.00',
        isRateValid: true,
        isTermValid: true,
        loading: false,
        mainContainerClass: `col-md-6 ${this.mainContainer}`,
        money: {
          decimal: '.',
          masked: false,
          precision: 2,
          prefix: '',
          suffix: '',
          thousands: ',',
        },
        rateErrorMessage: '',
        ratesAndTerms: [],
        rateTermSelected: null,
        termErrorMessage: '',
      };
    },
    computed: {
      ...mapState('app', [
        'investmentsRestrictions',
      ]),
    },
    props: {
      mainContainer: String,
      predefinedRatesTerms: Array,
      rate: Number,
      rateLimit: Number,
      showDefaultValue: Boolean,
      term: Number,
      manageRateAndTermGroupsId: Number,
      isCurrentRateAndTerm: {
        type: Boolean,
        default: false,
      },
      nt: {
        type: Object,
        default: {},
        required: false,
      },
    },
    directives: { money: VMoney },
    methods: {
      async getInvestmentOptions(manageRateAndTermGroupsId) {
        return this.getRateAndTermByGroup({
          manageRateAndTermGroupsId,
        }).then(data => ({
          overrideManageRateAndTermGroupsId: data.overrideManageRateAndTermGroupsId,
          investmentOptions: data.investmentOptions,
        }));
      },
      setRateAndTerm() {
        if (!this.rateTermSelected) {
          this.rateTermSelected = this.ratesAndTerms[0];
        }

        const { term, rate } = this.rateTermSelected;

        this.$emit(Events.RATE_CHANGED, rate);
        this.$emit(Events.TERM_CHANGED, term);
        this.$emit(Events.RATE_AND_TERM_IS_VALID);
      },
      isAValidTerm() {
        if (!this.customRateAndTermCheckbox) {
          this.termErrorMessage = '';
          this.isTermValid = true;
          return true;
        }

        if (this.editedTerm < 0.5 || this.editedTerm % 0.25 !== 0) {
          this.termErrorMessage = 'Years must be a positive number greater than 0.5 in multiples of .25';
          this.isTermValid = false;
          return false;
        }

        this.isTermValid = true;
        return true;
      },
      ...mapActions('app', [
        'getRateAndTermByGroup',
      ]),
      isAValidRate() {
        if (!this.customRateAndTermCheckbox) {
          this.rateErrorMessage = '';
          this.isRateValid = true;
          return true;
        }

        if (this.editedRate < 0) {
          this.rateErrorMessage = 'Negative numbers are not allowed';
          this.isRateValid = false;
          return false;
        }

        if (this.editedRate > this.rateLimit) {
          this.rateErrorMessage =
            `The maximum allowed rate is ${formatRate(this.rateLimit)}`;
          this.isRateValid = false;
          return false;
        }

        this.isRateValid = true;
        return true;
      },
      emitValidation() {
        const event = (this.isRateValid && this.isTermValid)
          ?
            Events.RATE_AND_TERM_IS_VALID
          :
            Events.RATE_AND_TERM_IS_INVALID;

        this.$emit(event);
      },
      emitRateChange() {
        if (this.isAValidRate()) {
          this.$emit('rateChanged', Number(this.editedRate));
        }
      },
      emitTermChange() {
        if (this.isAValidTerm()) {
          this.$emit('termChanged', Number(this.editedTerm) * 12);
        }
      },
    },
    watch: {
      customRateAndTermCheckbox(newValue) {
        if (!newValue) {
          this.setRateAndTerm();
        } else {
          this.emitRateChange();
          this.emitTermChange();
          this.emitValidation();
        }
      },
      editedRate(newValue, oldValue) {
        if (newValue === oldValue) {
          return;
        }

        this.emitRateChange();
        this.emitValidation();
      },
      editedTerm(newValue, oldValue) {
        if (newValue === oldValue) {
          return;
        }

        this.emitTermChange();
        this.emitValidation();
      },
    },
  };
</script>

<style scoped>
  .content-heading {
    font-weight: normal;
  }
  .panel-default {
    padding: 20px 30px;
  }
  .panel-heading {
    color: #444;
    background-color: #ffffff;
    border-color: #cfdbe2;
    border-bottom: 0;
    padding: 0;
  }
  .alert {
    padding: 20px 10px 20px 10px;
  }
  label.title {
    color: #909FA6;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  label.value {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .line-separator {
    padding: 8px;
    line-height: 1.52857143;
    vertical-align: top;
    border-top: 1px solid #eee;
  }
  select, input {
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    height: 40px;
  }
  .notification {
    margin-top: 20px;
  }
  .transation-info {
    margin: 20px 0px 20px 0px;
  }
  .transation-reinvest {
    margin: 10px 20px 10px 20px;
  }
  .transaction-subtitle {
    margin-top: 20px;
  }
  .line-separator {
    padding: 8px;
    line-height: 1.52857143;
    vertical-align: top;
    border-top: 1px solid #eee;
  }
  .top-margin {
    margin-top: 2%;
  }
  .term-error-message {
    padding-left: 20px;
  }
  .bottom-margin {
    margin-bottom: 5px;
  }
  .left-padding {
    padding-left: 0;
  }
  .subtitle {
    margin: 7px 0 7px 0;
    color: #817E7D;
  }
  .first-row {
    margin-bottom: 10px;
  }
  .checkbox {
    margin-bottom: 15px;
    margin-left: 5px;
  }
  .btn {
    margin-left: 5px;
  }
  .no-transactions {
    color: #F78A0E;
  }
  .hyperlink {
    color: #f05050;
    text-decoration: underline;
  }
  .text-blue {
    color: #23b7e5;
  }
  .panel-title .fa {
    margin-right: 10px;
  }
  .panel .panel .panel-title {
    margin-bottom: 15px;
    font-size: 16px;
  }
  .checkbox {
    margin-left: 10px;
  }
  .c-checkbox span,
  .c-radio span {
    margin-left: -28px;
  }
  .col-md-2 .form-group > span {
    display: block;
    padding-top: 12px;
  }
</style>



// WEBPACK FOOTER //
// CustomRateAndTerm.vue?3d4b660b